import { Service } from '@toeverything/infra';
import type { Subscription } from 'rxjs';

import type { IViewportController, ViewportState } from '../types';
import type { FullscreenService } from './fullscreen.service';
import type { PresentationStateMachine } from './presentation-state-machine';
import type { WorkspaceRealtimeService } from './workspace-realtime.service';

/**
 * Response time for smooth viewport interpolation. A time-based factor avoids
 * different behavior on 60Hz vs 120Hz displays while keeping following calm.
 */
const VIEWPORT_RESPONSE_MS = 70;

/**
 * Snap threshold for lerp animation
 * When viewport is within this distance, snap to final position
 */
const SNAP_THRESHOLD = 0.5;

/**
 * Tool controller interface for presentation mode
 */
interface IToolController {
  enterPresentationMode(): void;
  exitPresentationMode(): void;
  setFrameIndex(index: number): void;
}

/**
 * FollowerViewportSyncService (sl-ck8v: Socket-based sync)
 *
 * Single Responsibility: Synchronizing viewport with presenter
 *
 * Modes:
 * - Free Mode: Lerp-based smooth viewport sync
 * - Frame Mode: Navigate to frame (fit to screen)
 *
 * sl-ck8v: Now uses server-pushed updates instead of polling.
 * This is more efficient and fixes fullscreen sync issues.
 *
 * Depends on:
 * - PresentationStateMachine (Single Source of Truth for local state)
 * - WorkspaceRealtimeService (Server-pushed viewport/frame updates)
 */
export class FollowerViewportSyncService extends Service {
  private stateSubscription?: Subscription;
  private fullscreenSubscription?: Subscription;
  private viewportSubscription?: Subscription;
  private frameSubscription?: Subscription;

  // Controllers (set by Editor)
  private viewportController: IViewportController | null = null;
  private toolController: IToolController | null = null;

  // Lerp animation state
  private targetViewport: ViewportState | null = null;
  private animationFrameId: number | null = null;
  private lastAnimationTimestamp: number | null = null;

  // Track previous state for transitions
  private wasFollowing = false;
  private wasInFrameMode = false;
  private isInPresentationMode = false;

  constructor(
    private readonly stateMachine: PresentationStateMachine,
    private readonly fullscreenService: FullscreenService,
    private readonly socketService: WorkspaceRealtimeService
  ) {
    super();
    this.setupSubscriptions();
  }

  /**
   * Bind viewport controller (called by Editor when edgeless is mounted)
   */
  bindViewportController(controller: IViewportController): () => void {
    this.viewportController = controller;
    return () => {
      this.viewportController = null;
    };
  }

  /**
   * Bind tool controller (called by Editor when edgeless is mounted)
   */
  bindToolController(controller: IToolController): () => void {
    this.toolController = controller;

    // The following state can arrive before the edgeless tool controller is
    // mounted. Materialize that state as soon as the controller becomes ready.
    if (this.stateMachine.state$.value.type === 'following_frames') {
      this.enterPresentationMode();
    }

    return () => {
      this.isInPresentationMode = false;
      this.toolController = null;
    };
  }

  private setupSubscriptions(): void {
    // Main state subscription for tracking following state
    this.stateSubscription = this.stateMachine.state$.subscribe(state => {
      const isFollowing =
        state.type === 'following_free' || state.type === 'following_frames';
      const isInFrameMode = state.type === 'following_frames';

      // Handle start/stop following
      if (isFollowing && !this.wasFollowing) {
        this.onStartFollowing();
      } else if (!isFollowing && this.wasFollowing) {
        this.onStopFollowing();
      }

      // Handle mode changes
      if (isInFrameMode && !this.wasInFrameMode) {
        this.onEnterFrameMode();
      } else if (!isInFrameMode && this.wasInFrameMode) {
        this.onExitFrameMode();
      }

      // Frame following is independent from browser fullscreen.
      if (state.type === 'following_frames') {
        this.syncToFrame(state.frameIndex);
      }

      this.wasFollowing = isFollowing;
      this.wasInFrameMode = isInFrameMode;
    });

    // Re-fit the active frame after fullscreen changes its viewport geometry.
    // Session management remains owned by FollowerInputService.
    this.fullscreenSubscription =
      this.fullscreenService.isFullscreen$.subscribe(() => {
        const state = this.stateMachine.state$.value;
        if (state.type === 'following_frames' && this.isInPresentationMode) {
          this.syncToFrame(state.frameIndex);
        }
      });

    // sl-ck8v: Subscribe to server-pushed viewport updates (free mode)
    this.viewportSubscription = this.socketService.presenterViewport$.subscribe(
      viewport => {
        if (!viewport) return;

        const state = this.stateMachine.state$.value;
        // Only sync in free mode
        if (state.type !== 'following_free') return;

        this.syncToViewportLerp(viewport);
      }
    );

    // sl-ck8v: Subscribe to server-pushed frame updates (frame mode)
    this.frameSubscription = this.socketService.presenterFrame$.subscribe(
      frame => {
        if (!frame) return;

        const state = this.stateMachine.state$.value;
        if (state.type !== 'following_frames') return;

        this.syncToFrame(frame.frameIndex);
      }
    );
  }

  private onStartFollowing(): void {
    // sl-ck8v: No polling needed - socket events handle updates
    console.log('[FollowerViewportSync] Started following (socket-based)');
  }

  private onStopFollowing(): void {
    this.stopAnimation();
    if (this.isInPresentationMode) {
      this.exitPresentationMode();
    }
  }

  private onEnterFrameMode(): void {
    this.stopAnimation();
    this.enterPresentationMode();
  }

  private onExitFrameMode(): void {
    if (this.isInPresentationMode) {
      this.exitPresentationMode();
    }
  }

  /**
   * Smooth viewport sync (Free Mode) using Lerp interpolation
   */
  private syncToViewportLerp(viewport: ViewportState): void {
    if (!this.viewportController) return;

    this.targetViewport =
      viewport.visibleBounds && this.viewportController.getViewportForBounds
        ? this.viewportController.getViewportForBounds(viewport.visibleBounds)
        : viewport;

    // Start animation if not already running
    if (this.animationFrameId === null) {
      this.animateViewport();
    }
  }

  /**
   * Animate viewport using Lerp (smooth ease-out)
   * Always uses this.targetViewport to get the LATEST target position
   */
  private animateViewport(): void {
    const tick = (timestamp: number) => {
      // Always check current state - targetViewport may have been updated
      if (!this.viewportController || !this.targetViewport) {
        this.animationFrameId = null;
        this.lastAnimationTimestamp = null;
        return;
      }

      // Get CURRENT target (may have changed since last frame)
      const target = this.targetViewport;
      const curr = this.viewportController.getCurrentViewport();
      const elapsed =
        this.lastAnimationTimestamp === null
          ? 16
          : Math.min(timestamp - this.lastAnimationTimestamp, 64);
      const lerpFactor = 1 - Math.exp(-elapsed / VIEWPORT_RESPONSE_MS);
      this.lastAnimationTimestamp = timestamp;

      // Lerp toward target
      const newZoom = curr.zoom + (target.zoom - curr.zoom) * lerpFactor;
      const newX = curr.centerX + (target.centerX - curr.centerX) * lerpFactor;
      const newY = curr.centerY + (target.centerY - curr.centerY) * lerpFactor;

      // Check if close enough to snap
      const distX = Math.abs(newX - target.centerX);
      const distY = Math.abs(newY - target.centerY);
      const distZoom = Math.abs(newZoom - target.zoom);

      if (
        distX < SNAP_THRESHOLD &&
        distY < SNAP_THRESHOLD &&
        distZoom < 0.001
      ) {
        // Snap to final position
        this.viewportController.setViewport(
          target.zoom,
          target.centerX,
          target.centerY
        );
        this.animationFrameId = null;
        this.lastAnimationTimestamp = null;
        return;
      }

      // Apply interpolated viewport
      this.viewportController.setViewport(newZoom, newX, newY);

      // Continue animation
      this.animationFrameId = requestAnimationFrame(tick);
    };

    this.animationFrameId = requestAnimationFrame(tick);
  }

  private stopAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.lastAnimationTimestamp = null;
    this.targetViewport = null;
  }

  /**
   * Sync to frame (Frame Mode)
   * Uses setFrameIndex to let PresentationToolbar handle navigation
   */
  private syncToFrame(frameIndex: number): void {
    if (!this.toolController) return;
    this.stopAnimation();
    this.toolController.setFrameIndex(frameIndex);
  }

  /**
   * Enter presentation mode (activate PresentTool)
   */
  private enterPresentationMode(): void {
    if (!this.toolController) return;
    this.toolController.enterPresentationMode();
    this.isInPresentationMode = true;

    // Sync to current frame immediately
    const state = this.stateMachine.state$.value;
    if (state.type === 'following_frames') {
      this.toolController.setFrameIndex(state.frameIndex);
    }
  }

  /**
   * Exit presentation mode (deactivate PresentTool)
   */
  private exitPresentationMode(): void {
    if (!this.toolController) return;
    this.toolController.exitPresentationMode();
    this.isInPresentationMode = false;
  }

  override dispose(): void {
    this.stopAnimation();
    this.stateSubscription?.unsubscribe();
    this.fullscreenSubscription?.unsubscribe();
    this.viewportSubscription?.unsubscribe();
    this.frameSubscription?.unsubscribe();
    super.dispose();
  }
}
